import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./StatsChart.module.css";

export type StatsDay = { date: string; views: number; interactions: number };

type Props = {
  days: StatsDay[];
};

const WIDTH = 640;
const HEIGHT = 220;
const PADDING = { top: 16, right: 16, bottom: 28, left: 32 };

/** Gestapeltes Flaechendiagramm: Views unten (blau), Interactions oben (gold).
 * Ohne externe Chart-Library — wir malen direkt SVG-Paths.
 */
export function StatsChart({ days }: Props) {
  const { i18n, t } = useTranslation();
  const locale = i18n.language || "de";

  const { viewsPath, totalPath, gridY, xTicks, yMax } = useMemo(() => {
    if (days.length === 0) {
      return { viewsPath: "", totalPath: "", gridY: [], xTicks: [], yMax: 0 };
    }
    const maxRaw = Math.max(1, ...days.map((d) => d.views + d.interactions));
    const yMax = niceCeiling(maxRaw);
    const innerW = WIDTH - PADDING.left - PADDING.right;
    const innerH = HEIGHT - PADDING.top - PADDING.bottom;
    const step = days.length > 1 ? innerW / (days.length - 1) : 0;
    const leftX = PADDING.left;
    const rightX = PADDING.left + innerW;
    const bottomY = PADDING.top + innerH;

    const xFor = (i: number) => leftX + step * i;
    const yFor = (v: number) => bottomY - (v / yMax) * innerH;

    // Flaeche = Polygon: [linkes unteres Eck, alle Datenpunkte,
    // rechtes unteres Eck, close]. Damit entsteht eine geschlossene Form
    // vom Nullpunkt jeder X-Koordinate bis zur Kurvenhoehe.
    const buildArea = (pick: (d: StatsDay) => number) => {
      const pts: string[] = [];
      pts.push(`M ${leftX} ${bottomY}`);
      days.forEach((d, i) => pts.push(`L ${xFor(i)} ${yFor(pick(d))}`));
      pts.push(`L ${rightX} ${bottomY}`);
      pts.push("Z");
      return pts.join(" ");
    };

    const viewsPath = buildArea((d) => d.views);
    const totalPath = buildArea((d) => d.views + d.interactions);

    const gridY = [0, 0.25, 0.5, 0.75, 1].map((frac) => ({
      y: bottomY - frac * innerH,
      label: Math.round(yMax * frac).toString(),
    }));

    const tickEvery = Math.max(1, Math.round(days.length / 5));
    const xTicks = days
      .map((d, i) => ({ x: xFor(i), label: formatTick(d.date, locale), i }))
      .filter((tick) => tick.i % tickEvery === 0 || tick.i === days.length - 1);

    return { viewsPath, totalPath, gridY, xTicks, yMax };
  }, [days, locale]);

  const totals = useMemo(
    () =>
      days.reduce(
        (acc, d) => ({
          views: acc.views + d.views,
          interactions: acc.interactions + d.interactions,
        }),
        { views: 0, interactions: 0 },
      ),
    [days],
  );

  if (days.length === 0 || (totals.views === 0 && totals.interactions === 0)) {
    return (
      <div className={styles.empty}>
        <strong>{t("stats.emptyTitle")}</strong>
        <span>{t("stats.emptyBody")}</span>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.swatchViews}`} />
          {t("stats.legendViews", { count: totals.views })}
        </span>
        <span className={styles.legendItem}>
          <span className={`${styles.swatch} ${styles.swatchInteractions}`} />
          {t("stats.legendInteractions", { count: totals.interactions })}
        </span>
      </div>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={t("stats.ariaLabel")}
      >
        {/* Grid + Y-Labels */}
        {gridY.map((line, idx) => (
          <g key={idx}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={line.y}
              y2={line.y}
              className={styles.gridLine}
            />
            <text x={PADDING.left - 8} y={line.y + 4} className={styles.axisLabel} textAnchor="end">
              {line.label}
            </text>
          </g>
        ))}
        {/* Flaechen */}
        <path d={totalPath} className={styles.areaInteractions} />
        <path d={viewsPath} className={styles.areaViews} />
        {/* X-Ticks */}
        {xTicks.map((t, idx) => (
          <text
            key={idx}
            x={t.x}
            y={HEIGHT - 8}
            textAnchor="middle"
            className={styles.axisLabel}
          >
            {t.label}
          </text>
        ))}
        {/* Y-Max-Anzeige oben */}
        <text x={PADDING.left - 8} y={PADDING.top + 4} className={styles.axisLabel} textAnchor="end">
          {yMax}
        </text>
      </svg>
    </div>
  );
}

function niceCeiling(value: number): number {
  if (value <= 5) return 5;
  if (value <= 10) return 10;
  const exp = Math.floor(Math.log10(value));
  const base = Math.pow(10, exp);
  const leading = value / base;
  const roundedLeading = leading <= 2 ? 2 : leading <= 5 ? 5 : 10;
  return roundedLeading * base;
}

function formatTick(isoDate: string, locale: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  if (isNaN(d.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(d);
}
